// Requests for signed-in people and MedSnapp's staff: what they send (the signed-in session's ID token, or the shared ops
// key from the link while the server still takes it), the profile, the team and the audit log, and hosting with a session.
// A 401 on a request made with a session means the server no longer takes it (removed from the team, or it ran out): the
// session is forgotten here, and the pages send the person back to sign in. Tokens and sessions are never logged.
import type { AccountPlan, AuditEvent, DailySettings, DailyView, HostAction, StaffMember, StaffProfile, StaffRole, StateSnapshot } from '@shared/types';
import { api, ApiError } from '@/lib/api';
import { HostActionError, actionBody, type HostActionOptions } from '@/host/actions';
import { endStaffSession } from './session';

/** What a staff request carries: the session when signed in, otherwise the ops key from the link. */
export interface StaffAuth { session?: string; opsKey?: string }

export function authHeaders(a: StaffAuth): Record<string, string> {
  if (a.session) return { authorization: `Bearer ${a.session}` };
  if (a.opsKey) return { 'X-Ops-Key': a.opsKey };
  return {};
}

/** A request the server refused because the session is no longer good (as opposed to a missing role, which is a 403). */
export const isSessionRefusal = (e: unknown) => e instanceof ApiError && e.status === 401;
/** A refused ops key or session: the person cannot use this page as they are. */
export const isDenied = (e: unknown) => e instanceof ApiError && (e.status === 401 || e.status === 403);

/** A staff request: adds the session or the ops key; a 401 on a session ends it. */
export async function staffApi<T>(path: string, auth: StaffAuth, init: RequestInit = {}): Promise<T> {
  try {
    return await api<T>(path, { ...init, headers: { ...authHeaders(auth), ...(init.headers as Record<string, string> | undefined) } });
  } catch (e) {
    if (auth.session && isSessionRefusal(e)) endStaffSession(auth.session);
    throw e;
  }
}

// ---------------------------------------------------------------------------
// The profile
// ---------------------------------------------------------------------------
const profileOf = (p: Partial<StaffProfile>): StaffProfile =>
  ({ sub: String(p.sub ?? ''), email: String(p.email ?? ''), name: String(p.name ?? ''), roles: Array.isArray(p.roles) ? p.roles : [], expiresAt: Number(p.expiresAt) });

/** GET /auth/me: the name and roles as they are now (roles can change while a session lasts). */
export async function fetchProfile(token: string): Promise<StaffProfile> {
  return profileOf(await staffApi<Partial<StaffProfile>>('/auth/me', { session: token }));
}

/** PUT /auth/me: the account's name, here and in Cognito. */
export async function updateName(token: string, name: string): Promise<StaffProfile> {
  return profileOf(await staffApi<Partial<StaffProfile>>('/auth/me', { session: token }, { method: 'PUT', body: JSON.stringify({ name }) }));
}

/** DELETE /auth/me with the address typed to confirm: the server bars the account at once and finishes the erasure in its own time. */
export async function deleteAccount(token: string, emailTyped: string): Promise<void> {
  await staffApi<unknown>('/auth/me', { session: token }, { method: 'DELETE', body: JSON.stringify({ email: emailTyped }) });
}

/** How long a sign-out waits for the server before it signs out here anyway. */
export const SIGN_OUT_PATIENCE_MS = 4000;

/**
 * POST /auth/sign-out: the server ends every session this member holds, on every device. Never throws, and gives up
 * after SIGN_OUT_PATIENCE_MS, so a dead network never keeps anyone signed in on this device: the caller clears the
 * session here whatever this says (true when the server confirmed).
 */
export async function signOutOnServer(token: string): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const patience = new Promise<boolean>((resolve) => { timer = setTimeout(() => resolve(false), SIGN_OUT_PATIENCE_MS); });
  try {
    return await Promise.race([api<unknown>('/auth/sign-out', { method: 'POST', token }).then(() => true, () => false), patience]);
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// The team and the audit log (admins)
// ---------------------------------------------------------------------------
const ROLES: readonly StaffRole[] = ['admin', 'manager', 'staff', 'clinician', 'ops', 'teacher', 'creator'];
const PLANS: readonly AccountPlan[] = ['free', 'member'];
/** A member as the page needs it, or undefined for anything that is not one. */
function member(raw: unknown): StaffMember | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  if (typeof r.email !== 'string' || !r.email) return undefined;
  return {
    email: r.email, name: typeof r.name === 'string' ? r.name : '',
    roles: Array.isArray(r.roles) ? ROLES.filter((x) => (r.roles as unknown[]).includes(x)) : [],
    active: r.active !== false, addedAt: Number(r.addedAt) || 0, addedBy: typeof r.addedBy === 'string' ? r.addedBy : '',
    ...(typeof r.lastSignInAt === 'number' ? { lastSignInAt: r.lastSignInAt } : {}),
    ...(PLANS.includes(r.plan as AccountPlan) ? { plan: r.plan as AccountPlan } : {}),
    ...(r.selfServe === true ? { selfServe: true } : {}),
    ...(r.suspended === true ? { suspended: true } : {}),
    ...(typeof r.invitedAt === 'number' ? { invitedAt: r.invitedAt } : {}),
    ...(r.invitation === 'sent' || r.invitation === 'limit' || r.invitation === 'failed' ? { invitation: r.invitation } : {}),
  };
}
function event(raw: unknown): AuditEvent | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  if (typeof r.at !== 'number' || typeof r.action !== 'string') return undefined;
  return {
    at: r.at, actor: typeof r.actor === 'string' ? r.actor : '', action: r.action,
    ...(typeof r.code === 'string' && r.code ? { code: r.code } : {}), ...(typeof r.detail === 'string' && r.detail ? { detail: r.detail } : {}),
  };
}
/** The list in a body that is the list itself or wraps it under one of these names. */
function listIn(body: unknown, ...names: string[]): unknown[] {
  if (Array.isArray(body)) return body;
  if (body && typeof body === 'object') for (const n of names) { const v = (body as Record<string, unknown>)[n]; if (Array.isArray(v)) return v; }
  return [];
}

export type MemberDraft = Pick<StaffMember, 'email' | 'name' | 'roles' | 'active'> & Partial<Pick<StaffMember, 'plan' | 'suspended'>>;
export const cleanEmail = (e: string) => e.trim().toLowerCase();

export const team = {
  /** GET /staff */
  list: async (auth: StaffAuth): Promise<StaffMember[]> =>
    listIn(await staffApi<unknown>('/staff', auth), 'staff', 'members', 'items').map(member).filter((m): m is StaffMember => Boolean(m)),
  /** PUT /staff: adds, changes or brings back a member (never the bookkeeping fields). */
  save: async (auth: StaffAuth, m: MemberDraft): Promise<StaffMember | undefined> => {
    const body: MemberDraft = {
      email: cleanEmail(m.email), name: m.name.trim(), roles: ROLES.filter((r) => m.roles.includes(r)), active: m.active,
      ...(m.plan && PLANS.includes(m.plan) ? { plan: m.plan } : {}), ...(typeof m.suspended === 'boolean' ? { suspended: m.suspended } : {}),
    };
    return member(await staffApi<unknown>('/staff', auth, { method: 'PUT', body: JSON.stringify(body) }));
  },
  /** POST /staff/invite (12 Oct): emails the invitation again. */
  invite: async (auth: StaffAuth, email: string): Promise<StaffMember | undefined> =>
    member(await staffApi<unknown>('/staff/invite', auth, { method: 'POST', body: JSON.stringify({ email: cleanEmail(email) }) })),
  /** DELETE /staff/{email}: the member can no longer sign in; nothing is deleted. */
  remove: (auth: StaffAuth, email: string) => staffApi<unknown>(`/staff/${encodeURIComponent(cleanEmail(email))}`, auth, { method: 'DELETE' }),
  /** GET /staff/audit, newest first. */
  audit: async (auth: StaffAuth): Promise<AuditEvent[]> =>
    listIn(await staffApi<unknown>('/staff/audit', auth), 'events', 'audit', 'items').map(event).filter((e): e is AuditEvent => Boolean(e)).sort((a, b) => b.at - a.at),
};

// ---------------------------------------------------------------------------
// Hosting with a session (a signed-in member of staff on /host/CODE without the host key)
// ---------------------------------------------------------------------------
/** POST /battles/{code}/host/{action} with the session; refusals carry the same reason as on the host key route. */
export async function sendStaffHostAction(code: string, session: string, action: HostAction, opts: HostActionOptions = {}): Promise<{ state?: StateSnapshot }> {
  try {
    return await staffApi<{ state?: StateSnapshot }>(`/battles/${encodeURIComponent(code)}/host/${action}`, { session }, {
      method: 'POST', ...actionBody(opts),
    });
  } catch (e) {
    if (!(e instanceof ApiError)) throw e;
    const body = e.body ?? {};
    const reason = typeof body.reason === 'string' ? body.reason : e.status === 409 && /clinical sign-off/i.test(e.message) ? 'signoff-pending' : undefined;
    throw new HostActionError(e.status, e.message, reason, body.overrideAllowed === true, body);
  }
}

// ---------------------------------------------------------------------------
// The daily battle (29 Oct 2026): super admins and admins
// ---------------------------------------------------------------------------
/** GET /staff/daily: the settings, the next start and the battle already made for it. */
export const getDaily = (session: string) => staffApi<DailyView>('/staff/daily', { session });
/** PUT /staff/daily: saves the settings; answers with the view as it now is. */
export const putDaily = (session: string, settings: DailySettings) =>
  staffApi<DailyView>('/staff/daily', { session }, { method: 'PUT', body: JSON.stringify(settings), headers: { 'content-type': 'application/json' } });
